/**
 * UGC Carousel Sound Control
 * Handles sound toggle functionality for UGC carousel videos
 */

/**
 * Get all video elements in a slide
 * @param {HTMLElement} slide - The slide element
 * @returns {HTMLVideoElement[]} Array of video elements
 */
function getVideosInSlide(slide) {
  const videoPlayer = slide.querySelector('video-player');
  if (!videoPlayer) return [];
  
  const videos = videoPlayer.querySelectorAll('video');
  return Array.from(videos);
}

/**
 * Mute all videos in a slide
 * @param {HTMLElement} slide - The slide element
 */
function muteSlideVideos(slide) {
  const videos = getVideosInSlide(slide);
  videos.forEach((video) => {
    video.muted = true;
  });
}

/**
 * Unmute all videos in a slide
 * @param {HTMLElement} slide - The slide element
 */
function unmuteSlideVideos(slide) {
  const videos = getVideosInSlide(slide);
  videos.forEach((video) => {
    video.muted = false;
  });
}

/**
 * Update sound state for all slides based on global sound enabled state
 * @param {HTMLElement} carousel - The carousel container
 * @param {boolean} soundEnabled - Whether sound is enabled globally
 * @param {number} currentSlideIndex - Index of the current active slide
 */
function updateCarouselSound(carousel, soundEnabled, currentSlideIndex) {
  const slides = carousel.querySelectorAll('.splide__slide');
  
  slides.forEach((slide, index) => {
    if (index === currentSlideIndex && soundEnabled) {
      unmuteSlideVideos(slide);
    } else {
      muteSlideVideos(slide);
    }
  });
}

/**
 * Initialize sound control for a UGC carousel
 * @param {HTMLElement} carouselContainer - The carousel container element
 */
function initializeUgcCarouselSound(carouselContainer) {
  const soundToggles = carouselContainer.querySelectorAll('[data-sound-toggle]');
  const splideElement = carouselContainer.querySelector('.splide');
  
  if (!soundToggles.length || !splideElement) return;
  
  let soundEnabled = false;
  let currentSlideIndex = 0;
  let splideInstance = null;
  
  /**
   * Get current active slide index
   * @returns {number} Current slide index
   */
  function getCurrentSlideIndex() {
    // Try to get from Splide instance
    if (splideInstance && typeof splideInstance.index === 'number') {
      return splideInstance.index;
    }
    
    // Fallback: find the active slide by class
    const activeSlide = carouselContainer.querySelector('.splide__slide.is-active');
    if (activeSlide) {
      const slideIndex = activeSlide.getAttribute('data-slide-index');
      return slideIndex ? parseInt(slideIndex, 10) : 0;
    }
    
    // Another fallback: check all slides for active state
    const slides = carouselContainer.querySelectorAll('.splide__slide');
    for (let i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains('is-active')) {
        return i;
      }
    }
    
    return 0;
  }
  
  /**
   * Handle slide change
   * @param {number} newIndex - New slide index
   */
  function handleSlideChange(newIndex) {
    const previousIndex = currentSlideIndex;
    currentSlideIndex = newIndex;
    
    // Mute previous slide
    if (previousIndex !== newIndex) {
      const previousSlide = carouselContainer.querySelector(
        `.splide__slide[data-slide-index="${previousIndex}"]`
      );
      if (previousSlide) {
        muteSlideVideos(previousSlide);
      }
    }
    
    // Update current slide based on sound state
    const currentSlide = carouselContainer.querySelector(
      `.splide__slide[data-slide-index="${currentSlideIndex}"]`
    );
    if (currentSlide) {
      if (soundEnabled) {
        unmuteSlideVideos(currentSlide);
      } else {
        muteSlideVideos(currentSlide);
      }
    }
  }
  
  /**
   * Update all sound toggle buttons to reflect current state
   * @param {boolean} enabled - Whether sound is enabled
   */
  function updateAllSoundToggles(enabled) {
    soundToggles.forEach((toggle) => {
      toggle.setAttribute('aria-pressed', enabled.toString());
    });
  }

  /**
   * Setup sound control event listeners
   */
  function setupSoundControl() {
    // Handle sound toggle button clicks (all buttons control the same global state)
    soundToggles.forEach((soundToggle) => {
      soundToggle.addEventListener('click', () => {
        soundEnabled = !soundEnabled;
        updateAllSoundToggles(soundEnabled);
        splideElement.setAttribute('data-sound-enabled', soundEnabled.toString());
        
        // Update current slide based on new sound state
        const currentIndex = getCurrentSlideIndex();
        handleSlideChange(currentIndex);
      });
    });
    
    // Listen for Splide slide change events
    // Splide emits 'splide:moved' event when a slide changes
    splideElement.addEventListener('splide:moved', (event) => {
      const newIndex = event.detail?.index ?? getCurrentSlideIndex();
      handleSlideChange(newIndex);
    });
    
    // Also listen for 'splide:move' event (before move) as fallback
    splideElement.addEventListener('splide:move', (event) => {
      const newIndex = event.detail?.index ?? getCurrentSlideIndex();
      handleSlideChange(newIndex);
    });
    
    // Fallback: Watch for active slide changes using MutationObserver
    const observer = new MutationObserver(() => {
      const newIndex = getCurrentSlideIndex();
      if (newIndex !== currentSlideIndex) {
        handleSlideChange(newIndex);
      }
    });
    
    // Observe changes to the is-active class on slides
    const slides = carouselContainer.querySelectorAll('.splide__slide');
    slides.forEach((slide) => {
      observer.observe(slide, {
        attributes: true,
        attributeFilter: ['class']
      });
    });
    
    // Initialize: ensure all videos start muted
    slides.forEach((slide) => {
      muteSlideVideos(slide);
    });
    
    // Get initial slide index
    currentSlideIndex = getCurrentSlideIndex();
  }
  
  // Wait for Splide to be initialized
  const waitForSplide = () => {
    // Check if Splide instance is available
    if (splideElement.splide) {
      splideInstance = splideElement.splide;
      setupSoundControl();
    } else if (window.Splide) {
      // Splide library is loaded, but instance might not be ready yet
      // Try to find it or wait a bit more
      setTimeout(() => {
        if (splideElement.splide) {
          splideInstance = splideElement.splide;
          setupSoundControl();
        } else {
          // Setup anyway with fallback methods
          setupSoundControl();
        }
      }, 200);
    } else {
      // Splide not loaded yet, wait and retry
      setTimeout(waitForSplide, 100);
    }
  };
  
  // Start waiting for Splide initialization
  waitForSplide();
}

/**
 * Initialize all UGC carousels with sound selectors
 */
function initializeAllUgcCarouselSounds() {
  const carousels = document.querySelectorAll('.ugc-carousel');
  
  carousels.forEach((carousel) => {
    const soundToggle = carousel.querySelector('[data-sound-toggle]');
    if (soundToggle) {
      initializeUgcCarouselSound(carousel);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Wait a bit for Splide to initialize
    setTimeout(initializeAllUgcCarouselSounds, 300);
  });
} else {
  // DOM already loaded
  setTimeout(initializeAllUgcCarouselSounds, 300);
}

// Also initialize for dynamically loaded content (Section Rendering API)
document.addEventListener('shopify:section:load', () => {
  setTimeout(initializeAllUgcCarouselSounds, 300);
});

